import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Inject, OnInit, Optional } from '@angular/core';
import { EmailViewerAttachment, EmailViewerMessage, EmailViewGet, EmailViewReply, EmailService } from '@app/services/email.service';
import { LayoutService } from '@app/core/services/layout.service';
import { ModalService } from '@app/core/services/modal.service';
import { TT_VIEW_NAVIGATOR, TTViewNavigator, useNavigatorFor } from '@app/modal/state-modal/state-modal.component';

@Component({
    selector: 'tt-email-view',
    templateUrl: './email-view.component.html',
    styleUrls: ['./email-view.component.css'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false,
})
export class EmailViewComponent implements OnInit {
    public emailKeyno?: string | number;

    public page?: EmailViewGet;

    public replies: EmailViewReply[] = [];

    public loadingReplies = false;

    public deleting = false;

    constructor(
        private emailService: EmailService,
        private modal: ModalService,
        private layout: LayoutService,
        private cdr: ChangeDetectorRef,
        @Optional() @Inject(TT_VIEW_NAVIGATOR) private navigator?: TTViewNavigator,
    ) {
        this.navigator = useNavigatorFor(navigator, this);
    }

    public ngOnInit(): void {
        if (this.navigator?.host === 'dialog') this.navigator.setHeading?.('email', true);
        const emailKeyno = this.navigator?.params['email_keyno'];
        if (typeof emailKeyno === 'string' || typeof emailKeyno === 'number') this.emailKeyno = emailKeyno;
    }

    public onMessageLoaded(message: EmailViewerMessage): void {
        this.page = message as EmailViewGet;
        if (this.navigator?.host === 'dialog') this.navigator.setHeading?.(message.heading || 'email', !message.heading);
        this.cdr.markForCheck();
        void this.loadReplies(message.email_keyno ?? this.emailKeyno);
    }

    public replyToEmail(event: MouseEvent): void {
        this.openReply(event, false);
    }

    public replyToAllEmail(event: MouseEvent): void {
        this.openReply(event, true);
    }

    private openReply(event: MouseEvent, replyAll: boolean): void {
        if (!this.emailKeyno) return;

        const target = {
            to: 'emailcreate',
            parms: {
                webpage: 'emailview',
                argtype: 'email_keyno',
                argvalue: this.emailKeyno,
                ...(replyAll ? { replyAll: true } : {}),
            },
        };

        if (event.ctrlKey || event.metaKey) {
            this.navigator?.newTab(target);
            return;
        }

        if (this.layout.getMediaQueries().tablet.matches) {
            const params = `emailview/email_keyno/${this.emailKeyno}${replyAll ? '?replyAll=true' : ''}`;
            void this.modal.openStateDialog({ state: 'emailcreate', params, width: 'min(96vw, 120rem)', height: '90vh' });
            return;
        }

        this.navigator?.go(target);
    }

    public openAttachment(event: { attachment: EmailViewerAttachment }): void {
        const { item_state, item_parms } = event.attachment;
        if (!item_state) return;

        void this.modal.openStateDialog({ state: item_state, params: item_parms || undefined });
    }

    public openSettings(event: MouseEvent): void {
        if (this.navigator?.host !== 'dialog') return;

        event.preventDefault();
        void this.modal.openStateDialog({ state: 'webpagesettingmenu', params: 'emailview' });
    }

    public async deleteEmail(): Promise<void> {
        if (!this.page || this.deleting) return;

        this.deleting = true;
        this.cdr.markForCheck();

        try {
            const confirmed = await this.modal.openConfirmDialog({
                type: 'danger',
                title: 'confirm_title',
                message: 'confirm_message',
                ok: 'confirm_ok_label',
                cancel: 'confirm_cancel_label',
            });

            if (!confirmed) return;

            const response = await this.emailService.deleteEmail(this.page);

            if (response && response.errorcode !== '0') {
                await this.modal.show({
                    type: 'warning',
                    title: 'error',
                    translateTitle: true,
                    message: response.errormessage,
                    translateMessage: false,
                });
                return;
            }

            this.navigator?.back();
        } finally {
            this.deleting = false;
            this.cdr.markForCheck();
        }
    }

    private async loadReplies(emailKeyno?: string | number): Promise<void> {
        if (emailKeyno === undefined || emailKeyno === '') {
            this.replies = [];
            return;
        }

        this.loadingReplies = true;
        this.cdr.markForCheck();

        try {
            this.replies = await this.emailService.getReplies({ email_keyno: emailKeyno });
        } catch {
            this.replies = [];
        } finally {
            this.loadingReplies = false;
            this.cdr.markForCheck();
        }
    }
}
